import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

export interface UserChipUser {
  displayName?: string | null
  email?: string | null
  photoURL?: string | null
}

export interface UserChipProps {
  user: UserChipUser | null | undefined
  size?: 'sm' | 'md' | 'lg'
  variant?: 'pill' | 'inline'
  maxNameLength?: number
  className?: string
}

const sizeConfig = {
  sm: {
    avatar: 'h-5 w-5',
    text: 'text-xs',
    padding: 'pl-2.5 pr-2 py-0.5',
    fallbackText: 'text-[10px]',
    gap: 'gap-1.5',
  },
  md: {
    avatar: 'h-6 w-6',
    text: 'text-sm',
    padding: 'pl-3 pr-2.5 py-1',
    fallbackText: 'text-xs',
    gap: 'gap-2',
  },
  lg: {
    avatar: 'h-8 w-8',
    text: 'text-sm',
    padding: 'pl-3.5 pr-3 py-1.5',
    fallbackText: 'text-xs',
    gap: 'gap-2',
  },
}

function getDisplayName(user: UserChipUser | null | undefined): string {
  if (!user) return 'Unknown'
  return user.displayName || user.email || 'Unknown'
}

function getInitial(user: UserChipUser | null | undefined): string {
  const name = getDisplayName(user)
  return (name[0] ?? '?').toUpperCase()
}

function truncateName(name: string, maxLength?: number): string {
  if (!maxLength || name.length <= maxLength) return name
  return `${name.slice(0, maxLength)}…`
}

export function UserChip({
  user,
  size = 'md',
  variant = 'pill',
  maxNameLength,
  className,
}: UserChipProps) {
  const config = sizeConfig[size]
  const displayName = getDisplayName(user)
  const truncatedName = truncateName(displayName, maxNameLength)

  return (
    <div
      className={cn(
        'flex items-center',
        config.gap,
        variant === 'pill' && [
          'rounded-full bg-muted/50 border border-border/50',
          config.padding,
        ],
        className
      )}
    >
      <Avatar className={cn(config.avatar, 'shrink-0')}>
        <AvatarImage src={user?.photoURL ?? undefined} alt={displayName} />
        <AvatarFallback className={config.fallbackText}>
          {getInitial(user)}
        </AvatarFallback>
      </Avatar>
      <span className={cn(config.text, 'font-medium truncate pr-1')}>
        {truncatedName}
      </span>
    </div>
  )
}
